/**
 * Article Component - Historical Content Section
 * 
 * Author: Markus van Kempen - markus.van.kempen@gmail.com
 * Last Updated: January 2025
 * Version: 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from './ToastContainer';
import { useAuth } from '../context/AuthContext';
import { getIcon } from '../utils/icons';
import ArticleImportModal from './ArticleImportModal';
import LoadingSpinner from './LoadingSpinner';
import PaginationBar from './PaginationBar';
import SEO from './SEO';
import './Article.css';
import {
  normalizeAuthorName,
  extractAuthorFromHtml,
  generateTags,
  articleBrowseItemMatchesSearchTerm,
} from '../utils/articleBrowseSearch';
import { scoreArticleSearchRelevance } from '../utils/siteSearch';

/** Featured articles first (newest updates first), then alphabetical by title. */
function sortArticlesForBrowse(items) {
  return [...items].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    if (a.featured && b.featured) {
      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      if (dateA !== dateB) return dateB - dateA;
    }
    const titleA = (a.title || '').toLowerCase();
    const titleB = (b.title || '').toLowerCase();
    return titleA.localeCompare(titleB);
  });
}

/** When searching, rank by title/phrase relevance (not featured / A–Z). */
function sortArticlesBySearchRelevance(items, searchText) {
  return [...items].sort((a, b) => {
    const scoreDiff =
      scoreArticleSearchRelevance(b, searchText) - scoreArticleSearchRelevance(a, searchText);
    if (scoreDiff !== 0) return scoreDiff;
    const titleA = (a.title || '').toLowerCase();
    const titleB = (b.title || '').toLowerCase();
    return titleA.localeCompare(titleB);
  });
}

/** sessionStorage key for article search/filter state */
const ARTICLES_STATE_KEY = 'articles_filterState';

function loadArticlesFilterState() {
  try {
    const raw = sessionStorage.getItem(ARTICLES_STATE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* ignore */ }
  return null;
}

/** Article content categories — stable reference for filters */
const ARTICLE_CATEGORIES = [
  { id: 'fiddle', name: 'Fiddle', icon: '🎻', tagMatches: ['fiddle', 'fiddler', 'violin', 'violinist'] },
  { id: 'cape breton', name: 'Cape Breton', icon: '🏴', tagMatches: ['cape breton', 'cape-breton', 'capebreton', 'nova scotia'] },
  { id: 'scottish', name: 'Scottish', icon: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', tagMatches: ['scottish', 'scotland', 'scots'] },
  { id: 'traditional', name: 'Traditional', icon: '🎵', tagMatches: ['traditional', 'trad', 'folk', 'folk music'] },
  { id: 'biography', name: 'Biography', icon: '👤', tagMatches: ['biography', 'biographies', 'biographical', 'memoir', 'life story'] },
  { id: 'history', name: 'History', icon: '📚', tagMatches: ['history', 'historical', 'heritage', 'past'] },
  { id: 'music', name: 'Music', icon: '🎶', tagMatches: ['music', 'musical', 'musician', 'musicians', 'song', 'songs', 'tune', 'tunes', 'recording', 'recordings', 'album', 'cd'] },
  { id: 'culture', name: 'Culture', icon: '🎭', tagMatches: ['culture', 'cultural', 'customs', 'traditions', 'society'] },
  { id: 'review', name: 'Reviews', icon: '⭐', tagMatches: ['review', 'reviews', 'reviewed', 'critique', 'criticism', 'recommendation'] }
];

/** Icon for /articles grid row from `content_pages.page_type` */
function articleBrowseTypeIcon(pageType) {
  switch (pageType) {
    case 'recording':
      return '💿';
    case 'biography':
      return '👤';
    case 'review':
      return '⭐';
    case 'news':
      return '📰';
    case 'article':
    default:
      return '📄';
  }
}

const Article = () => {
  const { showError } = useToast();
  const { currentUser, hasAnyRole } = useAuth();
  const navigate = useNavigate();

  const _saved = loadArticlesFilterState();

  const [searchTerm, setSearchTerm] = useState(_saved?.searchTerm ?? '');
  const [selectedCategories, setSelectedCategories] = useState(_saved?.selectedCategories ?? []);
  const [selectedAuthors, setSelectedAuthors] = useState(_saved?.selectedAuthors ?? []);
  const [articleContent, setArticleContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(_saved?.currentPage ?? 1);
  const [articlesPerPage, setArticlesPerPage] = useState(() => {
    // Load from localStorage or default to 12
    const saved = localStorage.getItem('articlesPerPage');
    return saved ? parseInt(saved, 10) : 12;
  });

  // Check if user can import articles or create articles
  const canImport = currentUser && hasAnyRole && hasAnyRole(['admin', 'super_admin', 'content_creator']);
  const canCreate = canImport;

  const handleCreateNewArticle = () => {
    // Generate a unique pageId for the new article
    const timestamp = Date.now();
    const pageId = `new-article-${timestamp}`;
    navigate(`/admin/pages/edit?pageId=${pageId}&type=article`);
  };

  // Debug logging
  useEffect(() => {
    console.log('🔍 Article Import Debug:', {
      currentUser: currentUser,
      hasAnyRole: typeof hasAnyRole,
      canImport: canImport,
      userRoles: currentUser?.roles
    });
  }, [currentUser, hasAnyRole, canImport]);

  // No hardcoded sample content - all articles come from database

  useEffect(() => {
    loadArticleContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist search/filter state so Back restores the results
  useEffect(() => {
    try {
      sessionStorage.setItem(ARTICLES_STATE_KEY, JSON.stringify({
        searchTerm,
        selectedCategories,
        selectedAuthors,
        currentPage,
      }));
    } catch (_) { /* storage unavailable — silently skip */ }
  }, [searchTerm, selectedCategories, selectedAuthors, currentPage]);

  const loadArticleContent = async () => {
    try {
      setLoading(true);

      const response = await fetch('/api/content/articles', {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to load articles: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 500));
        throw new Error('Server returned non-JSON response');
      }

      const pages = await response.json();

      // Need to fetch full content for each page to extract author
      const processedContentPromises = pages.map(async (page) => {
        try {
          const detailResponse = await fetch(`/api/content/pages/${page.page_id}`);
          let author = 'Paul S. Cranford';
          let detailData = null;

          if (detailResponse.ok) {
            detailData = await detailResponse.json();

            // Try to get author from metadata first, then extract from HTML
            if (detailData.metadata) {
              try {
                const parsedMeta = typeof detailData.metadata === 'string' ? JSON.parse(detailData.metadata) : detailData.metadata;
                if (parsedMeta.author) {
                  author = normalizeAuthorName(parsedMeta.author);
                } else {
                  author = extractAuthorFromHtml(detailData.html);
                }
              } catch (e) {
                console.error('Error parsing metadata for author:', e);
                author = extractAuthorFromHtml(detailData.html);
              }
            } else {
              author = extractAuthorFromHtml(detailData.html);
            }
          }

          // Parse metadata for publication date and tags
          let publicationYear = null;
          let parsedMetadata = {};

          // Use metadata from detailData (full page) if available, otherwise from page (list)
          const metadataSource = detailData?.metadata || page.metadata;

          if (metadataSource) {
            try {
              const metadata = typeof metadataSource === 'string' ? JSON.parse(metadataSource) : metadataSource;
              parsedMetadata = metadata;
              if (metadata.publicationDate) {
                publicationYear = new Date(metadata.publicationDate).getFullYear();
              }
            } catch (e) {
              console.error('Error parsing metadata for publication year:', e);
            }
          }

          // Generate tags from content (will use existing tags from metadata if available)
          // Use metadata from detailData (full page) which includes all tags
          const metadataForTags = detailData?.metadata || page.metadata;
          const tags = detailData
            ? generateTags(detailData.html, page.title || '', page.description || '', metadataForTags)
            : generateTags('', page.title || '', page.description || '', metadataForTags);

          const ratingCount =
            page.feedback_rating_count != null ? Number(page.feedback_rating_count) : 0;
          const avgRating =
            page.feedback_avg_rating != null ? Number(page.feedback_avg_rating) : null;

          return {
            id: page.page_id,
            title: page.title || page.page_id,
            category: 'article',
            type: page.page_type || 'article',
            pageType: page.page_type || 'article',
            description: page.description || '',
            url: `/page/${page.page_id}`,
            image: null,
            publicationYear: publicationYear,
            createdYear: page.created_at ? new Date(page.created_at).getFullYear() : null,
            updatedYear: page.updated_at ? new Date(page.updated_at).getFullYear() : null,
            updatedAt: page.updated_at || null,
            author,
            metadata: parsedMetadata,
            featured: page.featured === 1 || page.featured === true || detailData?.featured === 1 || detailData?.featured === true,
            tags: tags,
            feedbackAverageRating: avgRating,
            feedbackRatingCount: ratingCount,
          };
        } catch (err) {
          console.error(`Error fetching details for ${page.page_id}:`, err);
          // Parse metadata for publication date even on error
          let publicationYear = null;
          let parsedMetadata = {};
          if (page.metadata) {
            try {
              const metadata = typeof page.metadata === 'string' ? JSON.parse(page.metadata) : page.metadata;
              parsedMetadata = metadata;
              if (metadata.publicationDate) {
                publicationYear = new Date(metadata.publicationDate).getFullYear();
              }
            } catch (e) {
              // Ignore parse errors
            }
          }

          // Generate tags from available data
          const tags = generateTags('', page.title || '', page.description || '', page.metadata);

          const ratingCountErr =
            page.feedback_rating_count != null ? Number(page.feedback_rating_count) : 0;
          const avgRatingErr =
            page.feedback_avg_rating != null ? Number(page.feedback_avg_rating) : null;

          return {
            id: page.page_id,
            title: page.title || page.page_id,
            category: 'article',
            type: page.page_type || 'article',
            pageType: page.page_type || 'article',
            description: page.description || '',
            url: `/page/${page.page_id}`,
            image: null,
            publicationYear: publicationYear,
            createdYear: page.created_at ? new Date(page.created_at).getFullYear() : null,
            updatedYear: page.updated_at ? new Date(page.updated_at).getFullYear() : null,
            updatedAt: page.updated_at || null,
            author: normalizeAuthorName('Paul S. Cranford'),
            metadata: parsedMetadata,
            featured: page.featured === 1 || page.featured === true,
            tags: tags,
            feedbackAverageRating: avgRatingErr,
            feedbackRatingCount: ratingCountErr,
          };
        }
      });

      const processedContent = sortArticlesForBrowse(await Promise.all(processedContentPromises));

      // Debug: Log generated tags for each article
      console.log('📋 Articles with tags:', processedContent.map(article => ({
        title: article.title,
        tags: article.tags
      })));

      setArticleContent(processedContent);
      setFilteredContent(processedContent);
    } catch (error) {
      console.error('Error loading article content:', error);
      showError(`Failed to load articles: ${error.message}`);
      // Set empty arrays on error - no fallback content
      setArticleContent([]);
      setFilteredContent([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Filter content based on search term and categories
    let filtered = articleContent;

    // Filter by categories (multi-select) - check against tags and map tags to categories
    if (selectedCategories.length > 0) {
      console.log('🔍 Filtering by categories:', selectedCategories);

      filtered = filtered.filter(item => {
        const tags = item.tags || [];
        // Convert tags to array if it's a string
        const tagsArray = typeof tags === 'string'
          ? tags.split(',').map(t => t.trim().toLowerCase())
          : Array.isArray(tags)
            ? tags.map(t => t.toLowerCase())
            : [];

        // Check if any selected category matches
        // First check if category ID matches tag directly, then check tagMatches
        const matches = selectedCategories.some(categoryId => {
          // Check direct match
          if (tagsArray.some(tag => tag.includes(categoryId) || categoryId.includes(tag))) {
            return true;
          }

          // Check if any tag matches the category's tagMatches
          const category = ARTICLE_CATEGORIES.find(cat => cat.id === categoryId);
          if (category && category.tagMatches) {
            return category.tagMatches.some(match =>
              tagsArray.some(tag => tag.includes(match.toLowerCase()) || match.toLowerCase().includes(tag))
            );
          }

          return false;
        });

        return matches;
      });

      console.log(`📊 Filtered ${filtered.length} of ${articleContent.length} articles`);
    }

    // Filter by authors (with normalization)
    if (selectedAuthors.length > 0) {
      filtered = filtered.filter(item => {
        if (!item.author) return false;
        const normalizedAuthor = normalizeAuthorName(item.author);
        return selectedAuthors.includes(normalizedAuthor);
      });
    }


    // Filter by search term — token rules shared with Advanced Search (siteSearch.js)
    if (searchTerm.trim()) {
      filtered = filtered.filter(item => articleBrowseItemMatchesSearchTerm(item, searchTerm));
      setFilteredContent(sortArticlesBySearchRelevance(filtered, searchTerm));
    } else {
      setFilteredContent(sortArticlesForBrowse(filtered));
    }
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, selectedCategories, selectedAuthors, articleContent]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedAuthors([]);
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="article-container">
        <div className="article-header">
          <h1>Historical Articles & Celtic Music Research</h1>
          <p>Loading articles…</p>
        </div>
        <LoadingSpinner message="Loading articles..." />
      </div>
    );
  }

  // Extract unique authors from article content, reacting to current category filters
  const getUniqueAuthors = () => {
    let contentToSearch = articleContent;
    
    // Determine the available authors based strictly on the current active Category (so user doesn't hit zero results by selecting an irrelevant author)
    if (selectedCategories.length > 0) {
      contentToSearch = contentToSearch.filter(item => {
        const tags = item.tags || [];
        const tagsArray = typeof tags === 'string'
          ? tags.split(',').map(t => t.trim().toLowerCase())
          : Array.isArray(tags)
            ? tags.map(t => t.toLowerCase())
            : [];
            
        return selectedCategories.some(categoryId => {
          if (tagsArray.some(tag => tag.includes(categoryId) || categoryId.includes(tag))) {
            return true;
          }
          const category = ARTICLE_CATEGORIES.find(cat => cat.id === categoryId);
          if (category && category.tagMatches) {
            return category.tagMatches.some(match =>
              tagsArray.some(tag => tag.includes(match.toLowerCase()) || match.toLowerCase().includes(tag))
            );
          }
          return false;
        });
      });
    }

    const authors = new Set();
    contentToSearch.forEach(article => {
      if (article.author) {
        const normalized = normalizeAuthorName(article.author);
        if (normalized) {
          authors.add(normalized);
        }
      }
    });
    return Array.from(authors).sort();
  };


  // Calculate pagination
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = filteredContent.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(filteredContent.length / articlesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setArticlesPerPage(newItemsPerPage);
    setCurrentPage(1);
    localStorage.setItem('articlesPerPage', newItemsPerPage.toString());
  };

  return (
    <>
      <SEO
        title="Historical Articles & Celtic Music Research | Cranford Publications"
        description="Browse historical articles about Cape Breton fiddle music, traditional musicians, cultural preservation, and Celtic music history by Paul Stewart Cranford."
        canonical="/articles"
        keywords="Cape Breton music history, fiddle articles, Scottish music history, traditional music articles, Celtic music heritage, fiddle biographies, music reviews, Paul Cranford"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Historical Articles & Celtic Music Research',
          description:
            'Browse historical articles about Cape Breton fiddle music, traditional musicians, cultural preservation, and Celtic music history by Paul Stewart Cranford.',
          url: 'https://www.cranfordpub.ca/articles',
          mainEntity: {
            '@type': 'ItemList',
            name: 'Celtic Music Articles',
            numberOfItems: articleContent.length,
            itemListElement: articleContent.slice(0, 40).map((article, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              url: `https://www.cranfordpub.ca${article.url}`,
              name: article.title,
            })),
          },
        }}
      />
      <div className="article-container">
        <div className="article-header-compact">
          <div className="header-title-row">
            <h1>Historical Articles & Celtic Music Research</h1>
            <div className="header-actions">
              {canCreate && (
                <button
                  className="btn btn-primary btn-sm create-article-btn"
                  onClick={handleCreateNewArticle}
                  title="Create a new article"
                >
                  {getIcon('add')} Create New Article
                </button>
              )}
              {canImport && (
                <button
                  className="btn btn-primary btn-sm import-article-btn"
                  onClick={() => setShowImportModal(true)}
                  title="Import articles from file or URL"
                >
                  {getIcon('upload')} Import
                </button>
              )}
              {canImport && (
                <button
                  className="refresh-btn-compact"
                  onClick={() => loadArticleContent()}
                  title="Refresh articles list"
                  disabled={loading}
                >
                  🔄
                </button>
              )}
              {(selectedCategories.length > 0 || selectedAuthors.length > 0 || searchTerm) && (
                <button
                  className="clear-filters-btn"
                  onClick={clearAllFilters}
                  title="Clear all filters"
                >
                  ✕ Clear
                </button>
              )}
            </div>
          </div>
          <div className="search-bar-compact">
            <div className="search-bar-with-count">
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  placeholder="Search articles, authors, tags..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="search-input"
                />
                <div className="search-icon">🔍</div>
              </div>
              <div className="results-count-inline">
                Showing {filteredContent.length > 0 ? indexOfFirstArticle + 1 : 0}-{Math.min(indexOfLastArticle, filteredContent.length)} of {filteredContent.length} articles
              </div>
            </div>
          </div>
        </div>

        <div className="filter-toolbar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', padding: '0.5rem 0.75rem', background: 'var(--color-surface)', borderRadius: '6px', border: '1px solid var(--color-border)', flexWrap: 'wrap', alignItems: 'center' }}>
          
          <div className="filter-dropdown-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 180px' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>Category</label>
            <select 
              className="sort-select"
              style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-surface)' }}
              value={selectedCategories.length > 0 ? selectedCategories[0] : ''}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedCategories(val ? [val] : []);
              }}
            >
              <option value="">All Categories</option>
              {ARTICLE_CATEGORIES.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-dropdown-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 180px' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>Author</label>
            <select 
              className="sort-select"
              style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-surface)' }}
              value={selectedAuthors.length > 0 ? selectedAuthors[0] : ''}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedAuthors(val ? [val] : []);
              }}
            >
              <option value="">All Authors</option>
              {getUniqueAuthors().map(author => (
                <option key={author} value={author}>{author}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="article-content">
          {/* Content stats moved to search bar */}

          <div className="content-grid">
            {currentArticles.map(item => (
              <Link key={item.id} to={item.url} className={`article-item${item.featured ? ' featured-item' : ''}`}>
                <div className="item-content">
                  <div className="item-header">
                    <h3 className="item-title">
                      {item.featured && <span style={{ fontSize: '0.9em', marginRight: '6px' }}>⭐</span>}
                      {articleBrowseTypeIcon(item.pageType)}{' '}
                      {item.title}
                    </h3>
                    {item.feedbackRatingCount > 0 && item.feedbackAverageRating != null && (
                      <div
                        className="item-rating-line"
                        title={`${item.feedbackAverageRating} / 5 — ${item.feedbackRatingCount} rating${item.feedbackRatingCount === 1 ? '' : 's'}`}
                      >
                        <span className="item-rating-stars" aria-hidden="true">
                          {'★'.repeat(Math.round(item.feedbackAverageRating))}
                          {'☆'.repeat(Math.max(0, 5 - Math.round(item.feedbackAverageRating)))}
                        </span>
                        <span className="item-rating-score">
                          {Math.round(item.feedbackAverageRating * 10) / 10}
                        </span>
                        <span className="item-rating-count">({item.feedbackRatingCount})</span>
                      </div>
                    )}
                  </div>
                  {item.author && (
                    <div className="item-author">
                      <span className="author-icon">{getIcon('user')}</span>
                      <span className="author-name">{item.author}</span>
                    </div>
                  )}
                  <p className="item-description">{item.description}</p>
                  {item.publicationYear && (
                    <div className="item-year">
                      {getIcon('calendar')} {item.publicationYear}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {filteredContent.length === 0 && (
            <div className="no-results">
              <h3>No articles found</h3>
              <p>Try adjusting your search terms or category filter</p>
            </div>
          )}

          {filteredContent.length > 0 && (
            <PaginationBar
              ariaLabel="Article list pagination"
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredContent.length}
              indexOfFirst={indexOfFirstArticle + 1}
              indexOfLast={indexOfLastArticle}
              itemLabel="articles"
              pageSize={articlesPerPage}
              pageSizeOptions={[12, 24, 36, 48]}
              onPageChange={handlePageChange}
              onPageSizeChange={handleItemsPerPageChange}
            />
          )}
        </div>

        {/* Article Import Modal */}
        {showImportModal && (
          <ArticleImportModal
            onClose={() => setShowImportModal(false)}
            onSuccess={() => {
              setShowImportModal(false);
              loadArticleContent(); // Reload articles after successful import
            }}
          />
        )}
      </div>
    </>
  );
};

export default Article;
